import React from 'react';
import { MapPin, Clock, Phone, Star, Heart, Users, Award, Utensils } from 'lucide-react';
import { restaurantInfo, hours, reviews, services } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* About Hero */}
      <section className="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="hero-content">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                About Imani Food
              </h1>
              <div className="accent-line mb-6" style={{ margin: '0 0 1.5rem 0' }}></div>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Where authentic African and Antillean flavors meet Swiss hospitality. 
                Our story is one of passion, heritage, and the universal language of good food.
              </p>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={20} fill="#eab308" className="text-yellow-500" />
                    ))}
                  </div>
                  <span className="font-semibold text-gray-900">{restaurantInfo.rating}/5</span>
                </div>
                <div className="text-gray-400">•</div>
                <span className="text-gray-700 font-medium">{restaurantInfo.totalReviews} Reviews</span>
              </div>
            </div>

            <div className="image-rounded">
              <img 
                src="https://images.pexels.com/photos/33434038/pexels-photo-33434038.jpeg?w=600&h=400&fit=crop" 
                alt="Traditional African dining scene showing authentic cultural atmosphere"
                className="image-hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="accent-line mb-6" style={{ margin: '0 0 1.5rem 0' }}></div>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Imani Food was born from a simple dream: to share the authentic, 
                  soul-warming flavors of African and Antillean cuisine with the 
                  beautiful community of Meyrin and beyond.
                </p>
                <p>
                  Our name "Imani" means "faith" in Swahili, reflecting our faith 
                  in the power of traditional recipes, quality ingredients, and the 
                  joy that comes from sharing a meal prepared with love.
                </p>
                <p>
                  Every dish we serve carries the essence of home-style cooking, 
                  from the fragrant Thiep of Senegal to the hearty Pondu of Congo. 
                  We believe that food is more than sustenance – it's culture, 
                  memory, and community on a plate.
                </p>
                <p>
                  What makes us special is not just our authentic recipes, but our 
                  commitment to giving back. A portion of our profits supports 
                  humanitarian projects, making every meal you enjoy with us 
                  contribute to positive change.
                </p>
              </div>
            </div>
            <div className="card-custom text-center">
              <div className="image-rounded mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1636447155412-d57350005073?w=500&h=300&fit=crop" 
                  alt="Chef preparing authentic African cuisine at grill"
                  className="w-full h-64 object-cover"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Made with Love & Tradition
              </h3>
              <p className="text-gray-600">
                Every recipe has been passed down through generations, 
                preserving the authentic flavors and cooking techniques 
                that make our cuisine truly special.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Us Special
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From our authentic recipes to our commitment to community, 
              discover what sets Imani Food apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Utensils size={40} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Authentic Recipes</h3>
              <p className="text-gray-600 text-sm">
                Traditional recipes from across Africa and the Antilles, 
                prepared with authentic techniques and ingredients.
              </p>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <Heart size={40} className="text-orange-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Made with Love</h3>
              <p className="text-gray-600 text-sm">
                Every dish is prepared with care, passion, and respect 
                for culinary traditions passed down through generations.
              </p>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Users size={40} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Community Focus</h3>
              <p className="text-gray-600 text-sm">
                We're more than a restaurant – we're a gathering place 
                where diverse cultures come together over great food.
              </p>
            </div>

            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Award size={40} className="text-yellow-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Giving Back</h3>
              <p className="text-gray-600 text-sm">
                A portion of our profits supports humanitarian projects, 
                making every meal a contribution to positive change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Visit Us Today
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600">
              Conveniently located in Meyrin with accessible parking and seating
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Location Info */}
            <div className="card-custom">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Location & Contact</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Address</p>
                    <p className="text-gray-600">{restaurantInfo.address}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Wheelchair-accessible entrance, seating, and parking available
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone size={24} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Phone</p>
                    <p className="text-gray-600">{restaurantInfo.phone}</p>
                    <p className="text-sm text-gray-500">Call for reservations and takeaway orders</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="card-custom">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Opening Hours</h3>
              <div className="space-y-4">
                {Object.entries(hours).map(([day, time]) => (
                  <div key={day} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                    <span className={`font-medium ${
                      day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) 
                        ? 'text-gray-900' 
                        : 'text-gray-600'
                    }`}>
                      {day}
                    </span>
                    <span className={`${
                      day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) 
                        ? 'text-gray-900 font-semibold' 
                        : 'text-gray-600'
                    }`}>
                      {time}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3 text-green-700">
                  <Clock size={20} />
                  <span className="font-medium">
                    Currently Open - Closes at 10:00 PM
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600">
              Real experiences from our valued customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="card-custom">
                <div className="flex items-center justify-between mb-6">
                  <div className="stars">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.timeAgo}</span>
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{review.text}"
                </p>
                <div className="text-gray-900 font-semibold">
                  – {review.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services & Accessibility */}
      <section className="section-padding section-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Services & Accessibility
            </h2>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600">
              We're committed to serving everyone in our community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="card-custom">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Service Options</h3>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Dine-in</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Takeaway</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Delivery</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Catering</span>
                </li>
              </ul>
            </div>

            <div className="card-custom">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Accessibility</h3>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Wheelchair-accessible entrance</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Wheelchair-accessible seating</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Wheelchair-accessible parking</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Good for kids</span>
                </li>
              </ul>
            </div>

            <div className="card-custom">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Payment Methods</h3>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Credit cards</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Debit cards</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">NFC mobile payments</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Cash accepted</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;